import {
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  useNavigate,
  useParams,
} from 'react-router-dom'
import {
  Button,
  Card,
  Col,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip as TooltipBS,
} from 'react-bootstrap'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import backend from '@api/backend'
import Select from 'react-select'
import jsPDF from 'jspdf'
import {
  useAcknowledgeAlertsMutation,
  useActiveAlertsQuery,
  useRefreshAlertGroup,
} from '@data/alerts/alerts'
import {
  AlertGroup,
  AlertStatus,
} from '@data/alerts/model/alert.model'
import { useAuthStore } from '@store/auth.store'
import { useVisibleDevices } from '@data/device/device'
import AckAlertModal from './AckAlertModal'
import { FaClock } from 'react-icons/fa'
import { BsBuildingFillSlash } from "react-icons/bs"
import { TbDeviceDesktopX } from 'react-icons/tb'
import { FiEyeOff } from "react-icons/fi"
import { TbAlertTriangleOff } from "react-icons/tb";
import SectorIcon from '../../general/SectorIcon/SectorIcon'
import WaterUseChart from './WaterUseChart'
import { UOM_OPTIONS } from '../DeviceManagement/DeviceInformationManager/constants'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'
import { buildAlertPDF } from './helper-functions'
import { MessageCircleMore } from 'lucide-react'
import {
  useAlertComments,
  useAlertCommentsCreation,
  useAlertCommentsUpdate,
} from '@data/comments/alertComments/alertComments'
import { IComments } from '@data/comments/model/comments.model'
import CommentComponent from '../Components/Comment/CommentComponent'

import './AlertQueueItem.scss'
import { MdGroupOff } from 'react-icons/md'
import { useVisibleLocations } from '@data/locations/locations'
import { TZDate } from '@date-fns/tz'
import { UTCDate } from '@date-fns/utc'
import {
  addHours,
  format,
  formatDistanceStrict,
} from 'date-fns'
import { TZDateTimeOverlay } from '@components/ui/Popovers'
import Device from '@data/device/model/device'
import CustomerLocation from '@data/locations/model/customerLocation'
import { DeviceModalMantine } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/DeviceModal'
import { hasPermissions } from '@common/utils/validation/authValidators'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LineController,
  PointElement,
  Filler,
)

interface IAlertQueueItemProps {
  alertGroupItem?: AlertGroup
  historical?: boolean
}

interface IAckAlertItem {
  alertGroup: AlertGroup
  priorityTier: number
  status: AlertStatus
}

interface EmergencyContact {
  name: string
  email: string
  mobile: string
}

// const plugin = {
//     id: 'afterRender',
//
//     afterRender: function(chart: any, options: any) {
//         console.log("After render")
//     }
// }S
// ChartJS.register(plugin);

function AlertQueueItem({ alertGroupItem, historical = false }: IAlertQueueItemProps) {
  const {
    isPending: isAcknowledging,
    mutateAsync: acknowledgeAlerts,
  } = useAcknowledgeAlertsMutation()
  const {
    isPending: isRefreshingGroup,
    mutate: refreshGroup,
  } = useRefreshAlertGroup()
  const permissions = useAuthStore((state) => state.permissions)
  const devices = useVisibleDevices()
  const { data: queue = [] } = useActiveAlertsQuery()
  const { data: userInfo } = useCurrentUserQuery()
  const locations = useVisibleLocations()

  const params = useParams()
  const navigate = useNavigate()
  const { id } = params

  const [estimatedLeak, setEstimatedLeak] = useState<number>(0)
  // const [estimatedLeakCost, setEstimatedLeakCost] = useState<number>(0);
  const [ackAlert, setAckAlert] = useState<IAckAlertItem>()
  const hasUpdateAlertPermission = hasPermissions(permissions, ['UPDATE:ALERTS:USAGE'])
  const [chartAnimationState, setChartAnimationState] = useState<boolean>(false)
  const [chartImage, setChartImage] = useState<string | null>(null)
  const [alertGroup, setAlertGroup] = useState<AlertGroup | undefined>(undefined)
  const [device, setDevice] = useState<Device | null>(null)
  const [deviceLocation, setDeviceLocation] = useState<CustomerLocation | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isLoadingEmergencyContacts, setIsLoadingEmergencyContacts] = useState<boolean>(false)
  const [emergencyContactResult, setEmergencyContactResult] = useState<
    null | string | EmergencyContact[]
  >(null)
  const [selectedUom, setSelectedUom] = useState(device?.deviceSettings.uom || 'Liters')
  const [displayAlertComments, setDisplayAlertComments] = useState(false)
  const [deviceModalShow, setDeviceModalShow] = useState(false)

  const { data: alertComments } = useAlertComments(alertGroup?.dUuid, alertGroup?.gid)

  const { mutate: createNewComment } = useAlertCommentsCreation()
  const { mutate: updateComment } = useAlertCommentsUpdate()

  useEffect(() => {
    if (!chartAnimationState) {
      const chartElement = document.getElementById('chart-element') as HTMLElement
      if (!chartElement) return
      const chartImgData = (chartElement.firstElementChild as HTMLCanvasElement).toDataURL(
        'image/png',
      )

      setChartImage(chartImgData)
    }
  }, [chartAnimationState])

  // Filter on dUuid rather than deviceId: a device's deviceId can change, its UUID cannot.
  const dUuidSet = useMemo(
    () => new Set(devices.map((d) => d.dUuid)),
    [devices],
  )

  const filteredQueue = useMemo(
    () => queue.filter((q) => dUuidSet.has(q.dUuid)),
    [queue, dUuidSet],
  )

  useEffect(() => {
    if (id) {
      const alert = filteredQueue.find((queue) => queue.gid === id)
      const alertDevice = devices.find((d) => d.dUuid === alert?.dUuid)
      if (alertDevice) {
        setDevice(alertDevice)
        setSelectedUom(alertDevice?.deviceSettings.uom)
        const location = locations.find((location) => location.id === alertDevice?.deviceLocationId)
        if (location) setDeviceLocation(location)
      }
      setAlertGroup(alert)
      setIsLoading(false)
      setEmergencyContactResult(null)
      setDisplayAlertComments(false)
    }
  }, [params, filteredQueue])

  useEffect(() => {
    if (alertGroupItem) {
      setAlertGroup(alertGroupItem)
      setIsLoading(false)
      const alertDevice = devices.find((d) => d.dUuid === alertGroupItem.dUuid)
      if (alertDevice) {
        setDevice(alertDevice)
        setSelectedUom(alertDevice?.deviceSettings.uom)
        const location = locations.find((location) => location.id === alertDevice?.deviceLocationId)
        if (location) setDeviceLocation(location)
      }
    }
  }, [alertGroupItem])

  useEffect(() => {
    if (displayAlertComments) {
      const element = document.getElementById('alertCommentsComponent')

      element?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [displayAlertComments])

  if (!alertGroup) return <div />

  function downloadWaterGraph() {
    const chartElement = document.getElementById('chart-element') as HTMLElement

    if (chartElement && device && alertGroup && alertGroup.priorityTier) {
      setIsDownloading(true)
      buildAlertPDF(
        chartElement,
        device,
        alertGroup.priorityTier,
        startAlertTime,
        endAlertTime,
        mostRecentAlert,
        alertDuration,
        alertClosed,
      )
        .then((pdf: jsPDF) => {
          const fileDate = new Date(alertGroup.startAlertTimeUTC)
          const fileName = `${device?.deviceName}_Alert_Tier${alertGroup?.priorityTier}_${format(fileDate, 'dd-MM-yyyy_HH:mm')}`
          pdf.save(fileName)
          setIsDownloading(false)
        })
        .catch((error) => {
          console.log('Something wrong happened while generating PDF: ', error)
          setIsDownloading(false)
        })
    }
  }

  function canAckAlert(alertGroup: AlertGroup) {
    return hasUpdateAlertPermission && alertGroup.status !== AlertStatus.CLOSED
  }

  const activeAlerts = alertGroup.alerts.filter((alert) => alert.priorityTier == null)
  const ackAlertList = alertGroup.alerts.filter((alert) => alert.priorityTier != null)

  const actionAlert = async (alertGroup: AlertGroup, priorityTier: number | null, status: AlertStatus) => {
    if (priorityTier === null) {
      return
    } else if (priorityTier !== 0) {
      setChartAnimationState(true)
      setAckAlert({
        alertGroup: alertGroup,
        priorityTier: priorityTier,
        status: status,
      })
    } else {
      try {
        // Redirect from the mutation's own success callback rather than after awaiting it.
        // Closing a group removes it from the active cache; if the navigation lands in a
        // later render the URL still points at the removed gid for one frame and the queue
        // flashes "Alert not found". Passing it here keeps the cache patch and the
        // navigation in the same update.
        await acknowledgeAlerts(
          {
            alertGroup,
            priorityTier,
            status,
            excludeUsage: false,
          },
          { onSuccess: redirect },
        )
      } catch {
        return
      }
    }
  }

  const redirect = () => {
    const currentAlertIndex = filteredQueue.findIndex((queue) => queue.gid === id)

    if (filteredQueue[currentAlertIndex + 1] !== undefined) {
      navigate(`/alert-queue/${filteredQueue[currentAlertIndex + 1].gid}`, { replace: true })
    } else {
      navigate('/alert-queue', { replace: true })
    }
  }

  const getEmergencyContact = async () => {
    setIsLoadingEmergencyContacts(true)

    try {
      const response = await backend.get(`/devices/${device?.deviceId}/emergency_contacts`)

      if (!response.data.length) {
        setEmergencyContactResult('Emergency Contact not found!')
      } else {
        const emergencyContacts = response.data.map((res: any) => ({
          name: res.firstName,
          mobile: res.mobile,
          email: res.email,
        }))
        setEmergencyContactResult(emergencyContacts)
      }
      setIsLoadingEmergencyContacts(false)
    } catch (e: any) {
      console.error(e)
      setEmergencyContactResult('Emergency Contact not found!')
      setIsLoadingEmergencyContacts(false)
    }
  }

  const handleNewComment = (newComment: string) => {
    const newCommentData = {
      device_id: alertGroup.dUuid,
      alertGroupId: alertGroup.gid,
      comment: newComment,
    }

    createNewComment(newCommentData)
  }

  const handleUpdateComment = (comment: IComments, updatedComment: string) => {
    const updatedCommentData = {
      device_id: alertGroup.dUuid,
      commentId: comment.id,
      comment: updatedComment,
      alertGroupId: alertGroup.gid,
    }
    updateComment(updatedCommentData)
  }

  const startAlertTime = format(TZDate.tz(device?.deviceSettings.timezone!, alertGroup.startAlertTimeUTC), 'EEE, dd/MM/yy h:mm a')
  const endAlertTime = format(TZDate.tz(device?.deviceSettings.timezone!, alertGroup.endAlertTimeUTC), 'EEE, dd/MM/yy h:mm a')
  const alertClosed = `Alert Closed: ${alertGroup.alertCloseDate ? format(TZDate.tz(device?.deviceSettings.timezone!, alertGroup.alertCloseDate), 'dd/MM/yy h:mm a') : ''}`
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const mostRecentAlert = `Most recent: ${formatDistanceStrict(new Date(alertGroup.endAlertTimeUTC), new UTCDate())}`
  const alertDuration = `Duration: ${formatDistanceStrict(new Date(alertGroup.endAlertTimeUTC), addHours(new Date(alertGroup.startAlertTimeUTC), -1), { unit: 'hour' })}`

  const onHideDeviceModal = () => {
    setDeviceModalShow(false)
  }
  return isLoading ? (
    <div className="spinner-border m-auto mb-5 mt-5 d-flex text-secondary" />
  ) : (
    <>
      <div className="alert-queue-item card shadow mb-4 mx-auto" style={{ width: '95%' }}>
        <div className="card-body">
          <div className="row p-1">
            <div className="col h-100">
              <div className={'d-flex h-100 align-items-center'}>
                <div className={'pr-3 h-100'}>
                  <SectorIcon
                    sector={device?.deviceSettings.sectorType}
                    size={25}
                    color="#000"
                    occupants={device?.deviceSettings.occupants}
                  />
                </div>
                <div className={'h-100 py-2'}>
                  <div>
                    <h5 className="m-0 font-weight-bold text-primary">
                      {device?.deviceName ? device.deviceName : alertGroup.deviceId}
                    </h5>
                    <h6
                      onClick={() => setDeviceModalShow(true)}
                      style={{
                        cursor: 'pointer',
                        color: 'blue',
                      }}
                    >
                      {alertGroup.deviceId}
                    </h6>
                  </div>
                  <div>
                    {deviceLocation && deviceLocation.suspended ?
                      <OverlayTrigger
                        delay={{ hide: 450, show: 300 }}
                        overlay={(props: any) => <TooltipBS {...props}>Location Suspended</TooltipBS>}
                        placement="bottom"
                      >
                        <BsBuildingFillSlash
                          color={'orange'}
                          size={20}
                          className={'mr-2'}
                        />
                      </OverlayTrigger>
                      : null
                    }
                    {device ?
                      <>
                        {device?.deviceSettings.deviceState === 'suspended' ?
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            overlay={(props: any) => <TooltipBS {...props}>Device Suspended</TooltipBS>}
                            placement="bottom"
                          >
                            <TbDeviceDesktopX
                              color={'orange'}
                              size={20}
                              className={'mr-2'}
                            />
                          </OverlayTrigger>
                          : null
                        }
                        {!device?.deviceSettings.monitoringEnabled ?
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            overlay={(props: any) => <TooltipBS {...props}>Monitoring Disabled</TooltipBS>}
                            placement="bottom"
                          >
                            <FiEyeOff
                              color={'orange'}
                              size={20}
                              className={'mr-2'}
                            />
                          </OverlayTrigger>
                          : null
                        }
                        {!device?.deviceSettings.aasEnabled ?
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            overlay={(props: any) => <TooltipBS {...props}>Auto Alerts Disabled</TooltipBS>}
                            placement="bottom"
                          >
                            <TbAlertTriangleOff
                              color={'orange'}
                              size={20}
                              className={'mr-2'}
                            />
                          </OverlayTrigger>
                          : null
                        }
                        {device?.deviceSettings.vacant ?
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            overlay={(props: any) => <TooltipBS {...props}>Vacant Asset</TooltipBS>}
                            placement="bottom"
                          >
                            <MdGroupOff
                              color={'orange'}
                              size={20}
                              className={'mr-2'} />
                          </OverlayTrigger>
                          : null
                        }
                      </>
                      : null
                    }
                  </div>
                </div>
              </div>
            </div>

            <div className="col-auto m-auto">
              {/* Re-reads this group from the device-scoped endpoint and splices it back
                  into the queue, so a single alert can be brought up to date without
                  refetching every alert. */}
              <button
                disabled={isRefreshingGroup || isAcknowledging}
                onClick={() => refreshGroup({
                  dUuid: alertGroup.dUuid,
                  gid: alertGroup.gid,
                })}
                type="button"
                className="mr-sm-0 mr-md-3 btn btn-outline-secondary my-md-0 my-2"
              >
                {isRefreshingGroup ? 'Refreshing…' : 'Refresh Alert'}
              </button>
            </div>
            <div className="col-auto m-auto">
              {canAckAlert(alertGroup) && alertGroup.priorityTier !== null ? (
                <button
                  onClick={() => {
                    actionAlert(alertGroup, alertGroup.priorityTier, AlertStatus.CLOSED)
                  }}
                  type="button"
                  className="mr-sm-0 mr-md-3 btn btn-primary my-md-0 my-2"
                >
                  Archive Alert
                </button>
              ) : null}
            </div>
            <div className="col-auto m-auto">
              <div className="btn-group " role="group">
                {canAckAlert(alertGroup) ? (
                  <button
                    disabled={!canAckAlert(alertGroup) || isAcknowledging}
                    onClick={() => {
                      actionAlert(alertGroup, 0, AlertStatus.CLOSED)
                    }}
                    type="button"
                    className={`btn ${
                      alertGroup.priorityTier === 0 ? 'btn-primary' : 'btn-outline-secondary'
                    }`}
                  >
                    Normal Flow
                  </button>
                ) : null}
                <button
                  disabled={!canAckAlert(alertGroup) || isAcknowledging}
                  onClick={() => {
                    actionAlert(alertGroup, 3, AlertStatus.IN_PROGRESS)
                  }}
                  type="button"
                  className={`btn ${
                    alertGroup.priorityTier === 3 ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                >
                  Tier 3
                </button>
                <button
                  disabled={!canAckAlert(alertGroup) || isAcknowledging}
                  onClick={() => {
                    actionAlert(alertGroup, 2, AlertStatus.IN_PROGRESS)
                  }}
                  type="button"
                  className={`btn ${
                    alertGroup.priorityTier === 2 ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                >
                  Tier 2
                </button>
                <button
                  disabled={!canAckAlert(alertGroup) || isAcknowledging}
                  onClick={() => {
                    actionAlert(alertGroup, 1, AlertStatus.IN_PROGRESS)
                  }}
                  type="button"
                  className={`btn ${
                    alertGroup.priorityTier === 1 ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                >
                  Tier 1
                </button>
              </div>
            </div>
          </div>

          <div className="row p-1">
            <div className="col m-auto">
              <div className={'row'}>
                <div className={'col-auto'}>
                  <div className={'d-flex h-100'}>
                    <div className={'pr-3 h-100'}>
                      <span className={'d-flex h-100 align-items-center'}>
                        <FaClock size={20} style={{ color: 'black' }}></FaClock>
                      </span>
                    </div>
                    <div className={'h-100 py-2'}>
                      <h5>
                        {hasUpdateAlertPermission ? (
                          <span
                            style={{
                              color: 'red',
                              fontWeight: 800,
                            }}
                          >
                            {' '}
                            {alertGroup.alerts.length} alerts ({activeAlerts.length} active /{' '}
                            {ackAlertList.length} acknowledged)
                            <br />
                          </span>
                        ) : null}
                        <span style={{ color: 'red', fontWeight: 400 }}>
                          {startAlertTime}
                        </span>
                        <small> to </small>
                        <span style={{ color: 'red', fontWeight: 400 }}>
                          {endAlertTime}
                        </span>
                      </h5>
                    </div>
                  </div>
                </div>
                <div className={'col-auto'}>
                  <h6>{mostRecentAlert}</h6>
                  <h6>{alertDuration}</h6>
                </div>

                <div className={'col-auto'}>
                  {historical && !hasUpdateAlertPermission ? null : (
                    <h6>
                      {alertGroup.hourlyAlertsSentDate
                        ? <>
                          {/*Alert Sent: {format(new Date(alertGroup.hourlyAlertsSentDate), 'dd/MM/yy h:mm a')}*/}
                          Alert
                          Sent: {format(TZDate.tz(device?.deviceSettings.timezone!, alertGroup.hourlyAlertsSentDate), 'dd/MM/yy h:mm a')}
                          <TZDateTimeOverlay
                            timestamp={new Date(alertGroup.hourlyAlertsSentDate)}
                            timezone={userTimezone}
                            title={'Alerts sent your time'}
                            timestampFormat={'dd/MM/yy h:mm a'}
                          />
                        </>
                        : 'No Alert Sent'}
                    </h6>
                  )}
                  <h6>
                    {alertGroup.alertCloseDate ?
                      <>
                        {alertClosed}
                        <TZDateTimeOverlay
                          timestamp={new Date(alertGroup.alertCloseDate)}
                          timezone={userTimezone}
                          title={'Alerts closed your time'}
                          timestampFormat={'dd/MM/yy h:mm a'}
                        />
                      </> : null}
                  </h6>

                  {hasUpdateAlertPermission ? <h6>Acknowledged By: {alertGroup.ackBy}</h6> : null}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1 row">
              <div className="col-sm-3 ml-2 mb-1 ">
                <div className="section-header">
                  <div className="">Unit of measure</div>
                </div>
                <Select
                  classNamePrefix="select"
                  value={{
                    value: selectedUom,
                    label: selectedUom,
                  }}
                  onChange={(e: any) => setSelectedUom(e.value)}
                  options={UOM_OPTIONS.map((uom) => {
                    return {
                      value: uom,
                      label:
                        uom === device?.deviceSettings?.uom
                          ? `${uom} (device default)`
                          : uom === userInfo?.preferences?.uom
                            ? `${uom} (your set preference)`
                            : uom,
                    }
                  })}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => `${option.label}`}
                  isClearable={false}
                />
              </div>

              <div
                className="col-sm-3 d-flex flex-row-reverse"
                style={{ maxHeight: '150px', overflowY: 'auto' }}
              >
                {emergencyContactResult ? (
                  typeof emergencyContactResult === 'string' ? (
                    emergencyContactResult
                  ) : (
                    <div>
                      <span>Emergency Contact(s) found:</span>
                      <hr />
                      {emergencyContactResult.map((contact, index) => (
                        <div className="emergency-contact d-flex flex-column ">
                          <span>{`name: ${contact.name}`}</span>

                          <span>{`email: ${contact.email}`}</span>

                          <span>{`mobile: ${contact.mobile}`}</span>
                          <hr />
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  hasPermissions(permissions, ['USER:ADMIN:SMARTFLOW']) && (
                    <Button onClick={getEmergencyContact} variant="outline-primary">
                      {isLoadingEmergencyContacts ? <Spinner size="sm" /> : 'Get emergency contact'}
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>

          <div className="col">
            <Card>
              <Card.Body className={'testing'}>
                {device ?
                  <WaterUseChart
                    alertGroup={alertGroup}
                    setEstimatedLeakCallback={setEstimatedLeak}
                    chartAnimationCallback={setChartAnimationState}
                    selectedUom={selectedUom}
                    chartId="chart-element2"
                    device={device}
                  /> : null}
                {/*<WaterWasteChart*/}
                {/*  show={false}*/}
                {/*  alertGroup={alertGroup}*/}
                {/*  selectedUom={selectedUom}*/}
                {/*  chartId="chart-element3"*/}
                {/*  />*/}
                <Row>
                  <Col xs={6}>
                    <Button
                      onClick={() => downloadWaterGraph()}
                      variant="secondary"
                      disabled={isDownloading || !alertGroup.priorityTier}
                      className="mt-4"
                    >
                      {isDownloading ? (
                        <span className="spinner-border spinner-border-sm text-primary"></span>
                      ) : (
                        <>
                          <span className="fas fa-download fa-lg mr-2"></span>
                          Download
                        </>
                      )}
                    </Button>
                  </Col>
                  {hasPermissions(permissions, ['ACCOUNT:ADMIN:SMARTFLOW']) && (
                    <Col xs={6} className="d-flex justify-content-end align-items-center">
                      <MessageCircleMore
                        role="button"
                        onClick={() => setDisplayAlertComments(!displayAlertComments)}
                        size={35}
                      />

                      {alertComments && alertComments.length > 0 && (
                        <span className="badge text-danger p-0 fs-6 mb-4">
                          {alertComments.length < 9 ? alertComments.length : '9+'}
                        </span>
                      )}
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
      {ackAlert && !chartAnimationState ? (
        <AckAlertModal
          alertGroup={ackAlert.alertGroup}
          priorityTier={ackAlert.priorityTier}
          status={ackAlert.status}
          onHide={() => setAckAlert(undefined)}
          chartImage={chartImage}
          selectedUom={selectedUom}
          activeAlertId={id || ''}
          filteredQueue={filteredQueue}
        />
      ) : null}

      <div
        id="alertCommentsComponent"
        className="p-0 ml-5 w-75"
        style={{ maxHeight: '400px', overflowY: 'scroll', overflowX: 'hidden' }}
      >
        {displayAlertComments && (
          <CommentComponent
            comments={alertComments}
            handleNewComment={handleNewComment}
            handleUpdateComment={handleUpdateComment}
          />
        )}
      </div>

      {deviceModalShow && device?
        <DeviceModalMantine
          opened={deviceModalShow}
          device={device}
          onClose={onHideDeviceModal}
        /> :
        null}
    </>
  )
}

export default AlertQueueItem
